/**
 * MagazineHub — Page d'entrée du format magazine éditorial (route /revision).
 *
 * Remplace l'ancienne page QCM "Révision" (modes noté/libre).
 * Devient la VITRINE des 9 dossiers ETM rédigés en format éditorial.
 *
 * Sources de contenu :
 *   - /app/frontend/src/data/etmIssues/index.js (ETM_ISSUES_INDEX)
 *   - /app/frontend/src/data/magazinesEnquetes.js (enquêtes publiées)
 *
 * URL canonique : /revision (préservé pour SEO + liens externes)
 * Liens sortants : /apprendre/:slug (article éditorial complet)
 */
import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight, Newspaper, BookOpen, Sparkles, Clock,
  Route, Flag, UserCheck, Users, Cog, Shield, Leaf, HeartPulse, GraduationCap,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ETM_ISSUES_INDEX } from '../data/etmIssues';
import { getPublishedEnquetes } from '../data/magazinesEnquetes';
import useSeo from '../hooks/useSeo';
import AudienceContextBanner from '../components/AudienceContextBanner';
import SecuriteRoutiereStats from '../components/SecuriteRoutiereStats';

const THEME_ICON_MAP = {
  R: Route, C: Flag, L: UserCheck, U: Users,
  D: BookOpen, M: Cog, P: Shield, E: Leaf, S: HeartPulse,
};

// Mapping des codes thème QCM legacy → slug magazine
const LEGACY_THEME_TO_SLUG = {
  R: 'route', C: 'circulation', L: 'conducteur', U: 'autres-usagers',
  D: 'notions-diverses', M: 'mecanique', P: 'epi', E: 'environnement',
  S: 'premiers-secours',
};

export default function MagazineHub() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Backward compat : ?theme=X&mode=free|exam → redirige vers /apprendre/<slug>
  useEffect(() => {
    const themeParam = searchParams.get('theme');
    if (themeParam && LEGACY_THEME_TO_SLUG[themeParam]) {
      navigate(`/apprendre/${LEGACY_THEME_TO_SLUG[themeParam]}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const publishedIssues = useMemo(
    () => ETM_ISSUES_INDEX.filter((i) => i.status === 'published'),
    [],
  );
  const comingSoonIssues = useMemo(
    () => ETM_ISSUES_INDEX.filter((i) => i.status !== 'published'),
    [],
  );
  const featuredEnquetes = useMemo(() => getPublishedEnquetes().slice(0, 6), []);

  useSeo({
    title: 'Magazine éditorial moto — Les 9 dossiers ETM · Code 2 Roues',
    description:
      "Le nouveau format magazine de Code 2 Roues : 9 dossiers éditoriaux sur les thématiques de l'Épreuve Théorique Moto (ETM). Lecture libre, sans pression, conçue par des moniteurs ECSR. La conduite expliquée comme un long-read, pas comme un QCM à rallonge.",
    keywords:
      'magazine moto, ETM 2026, format magazine, dossier éditorial, circulation moto, conducteur, équipement, premiers secours',
    path: '/revision',
  });

  return (
    <div className="min-h-screen" data-testid="magazine-hub-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* HERO */}
        <div className="mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 mb-4 text-[10px] font-black uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Le magazine · Édition 2026</span>
          </div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            data-testid="magazine-hub-title"
          >
            La conduite expliquée<br />
            comme un <span className="text-[hsl(var(--primary))]">long-read</span>,<br />
            pas comme un QCM.
          </h1>
          <p className="text-base sm:text-lg text-foreground/80 leading-relaxed max-w-3xl">
            Les <strong>9 thématiques de l'Épreuve Théorique Moto (ETM)</strong> rédigées en format
            <strong> magazine éditorial</strong> par des moniteurs ECSR. Photos, citations, encadrés
            pédagogiques, mises en situation réelles : la conduite comme un sujet de société.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5" /> {publishedIssues.length} dossiers publiés
            </span>
            <span className="opacity-50">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Newspaper className="w-3.5 h-3.5" /> {featuredEnquetes.length}+ enquêtes
            </span>
            <span className="opacity-50">·</span>
            <span>Lecture libre, sans chrono ni score</span>
          </div>
        </div>

        {/* Bandeau d'adaptation par audience (visible pour les 3 publics) */}
        <AudienceContextBanner
          pageHint="Le magazine éditorial s'adresse aux 3 publics moto : apprendre, enseigner, citer en oral CCS."
          hideOnEmpty
          contexts={{
            eleve: {
              title: 'Apprendre la conduite comme un sujet de société',
              bullets: [
                'Chaque dossier raconte une thématique ETM (Route, Circulation, EPI, etc.) en long-read.',
                'Lecture libre, sans chrono ni score — tu retiens parce que tu comprends.',
                'Photos, témoignages et encadrés pédagogiques pour mémoriser durablement.',
              ],
            },
            enseignant: {
              title: 'Réutiliser le magazine comme support de cours',
              bullets: [
                'Projette un dossier en salle pour ouvrir un sujet sous un angle éditorial.',
                'Utilise les enquêtes pour appuyer une statistique (ONISR) ou un cas réel.',
                'Croise les thématiques ETM avec ton programme du jour.',
              ],
              cta: 'Voir les 20 cours en salle',
              ctaTo: '/enseignant/salle',
            },
            'futur-enseignant': {
              title: 'Maîtriser les 9 thématiques ETM attendues au jury',
              bullets: [
                'Les 9 codes thèmes (R/C/L/U/D/M/P/E/S) sont régulièrement cités à l\'oral.',
                'Croise chaque thème avec un cas CCS pour préparer ta présentation.',
                'Repère les enquêtes terrain : sources de questions pour ta culture pédagogique.',
              ],
              cta: 'Aller au Hub Préparation CCS',
              ctaTo: '/preparation-ccs',
            },
          }}
          className="mb-10"
        />

        {/* Chiffres clés sécurité routière (variant complet, sous le banner audience) */}
        <SecuriteRoutiereStats variant="full" className="mb-10" />

        {/* COUVERTURE — Les 9 numéros */}
        <section className="mb-14">
          <div className="flex items-baseline justify-between mb-5">
            <h2
              className="text-2xl sm:text-3xl font-black"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Les 9 numéros ETM
            </h2>
            <Link
              to="/programme-etm"
              className="text-xs text-[hsl(var(--primary))] hover:underline font-black uppercase tracking-wider"
            >
              Voir le programme officiel →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {publishedIssues.map((it) => {
              const Icon = THEME_ICON_MAP[it.themeCode] || BookOpen;
              return (
                <Link
                  key={it.slug}
                  to={`/apprendre/${it.slug}`}
                  data-testid={`magazine-hub-issue-${it.slug}`}
                  className="group block"
                >
                  <Card className="h-full overflow-hidden hover:border-[hsl(var(--primary))]/60 transition-all hover:-translate-y-0.5">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div
                          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${it.accent}1a`, color: it.accent }}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                          N°{String(it.number).padStart(2, '0')}
                        </span>
                      </div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">
                        Thématique {it.themeCode}
                      </div>
                      <h3
                        className="font-black text-lg leading-tight mb-2 group-hover:text-[hsl(var(--primary))] transition-colors"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                      >
                        {it.title}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3 mb-3">
                        {it.teaser}
                      </p>
                      <div className="flex items-center justify-between text-[11px] font-bold">
                        {it.issue?.readTimeMin && (
                          <span className="inline-flex items-center gap-1 text-muted-foreground">
                            <Clock className="w-3 h-3" /> {it.issue.readTimeMin} min
                          </span>
                        )}
                        <span
                          className="inline-flex items-center gap-1 text-[hsl(var(--primary))]"
                        >
                          Lire le dossier <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          {comingSoonIssues.length > 0 && (
            <div className="mt-4 text-xs text-muted-foreground italic">
              {comingSoonIssues.length} numéro{comingSoonIssues.length > 1 ? 's' : ''} en cours de rédaction.
            </div>
          )}
        </section>

        {/* ENQUÊTES & TÉMOIGNAGES */}
        {featuredEnquetes.length > 0 && (
          <section className="mb-14">
            <div className="flex items-baseline justify-between mb-5">
              <h2
                className="text-2xl sm:text-3xl font-black"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                Enquêtes & Témoignages
              </h2>
              <Link
                to="/enquetes"
                className="text-xs text-[hsl(var(--primary))] hover:underline font-black uppercase tracking-wider"
              >
                Toutes les enquêtes →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredEnquetes.map((e) => (
                <Link
                  key={e.slug}
                  to={`/enquete/${e.slug}`}
                  data-testid={`magazine-hub-enquete-${e.slug}`}
                  className="group block"
                >
                  <Card className="h-full overflow-hidden hover:border-[hsl(var(--primary))]/60 transition-all hover:-translate-y-0.5">
                    <CardContent className="p-4">
                      <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-2">
                        Enquête
                      </div>
                      <h3
                        className="font-black text-base leading-tight mb-2 group-hover:text-[hsl(var(--primary))] transition-colors"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                      >
                        {e.title}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">
                        {e.teaser}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* FOOTER ÉDITORIAL */}
        <div className="text-center py-10 px-6 rounded-2xl bg-gradient-to-br from-[hsl(var(--primary))]/10 via-transparent to-transparent border border-border">
          <GraduationCap className="w-10 h-10 text-[hsl(var(--primary))] mx-auto mb-3" />
          <h3
            className="text-xl sm:text-2xl font-black mb-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Pourquoi ce changement ?
          </h3>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto mb-5">
            On a fait le choix d'abandonner le format banque de questions à rallonge. Notre conviction&nbsp;: on apprend mieux la conduite en comprenant le pourquoi qu'en cochant des cases.
          </p>
          <Button asChild variant="outline" data-testid="magazine-hub-manifesto">
            <Link to="/nouveau-format">
              Lire le manifeste <ArrowRight className="w-4 h-4 ml-1.5" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
